import PublicSiteServices from '@general/js/services/MembershipStatus/MembershipStatusServices';
import { formatFormDate, getCurrentDate, DHLError, axiosRequest, handleHttpResponse, isSuccessfulResponse } from '@general/js/utils';
import { Services } from '@/services/Services';
import PremiumCalculatorAbstractServices from './PremiumCalculatorAbstractServices';

export default class PremiumCalculatorServices extends PremiumCalculatorAbstractServices {
  /**
   * Returns reference data for premium calculator page in member portal
   *
   * @param {string} memberNumber member number (or also known as policy number)
   * @param {string} currentDate @optional date string in yyyy-mm-dd format. default to be current date if not given.
   * @returns {object} object of reference data
   */
  static async getPremiumCalculatorReferenceData(memberNumber, currentDate = getCurrentDate()) {
    try {
      const response = await axiosRequest().get(Services.MY_COVER.PREMIUM_CALCULATOR.GET_REFERENCE_DATA(memberNumber, currentDate), {
        params: {
          currentDate,
        },
      });
      if (handleHttpResponse(response) && response.data.referenceData) {
        return JSON.parse(JSON.stringify(response.data).tokenSwap());
      }
      throw new DHLError(`Unexpected data structure returned for data: ${JSON.stringify(response.data)}`);
    } catch (error) {
      throw new DHLError(`getPremiumCalculatorReferenceData() - Call failed with error: ${JSON.stringify(error.message)}`, error);
    }
  }

  /**
   * Take member input and return premium amount to pay or paid to date based on given type.
   *
   * @param {string} type @optional defines type of "member input" from ui. default to be `date`. should only pass either `date` or `amount`
   * @param {string} memberNumber member number (or also known as policy number)
   * @param {string} startDate date string in yyyy-mm-dd format
   * @param {string} memberInput defines what member has input from UI. depend on the @type, expected format is different.
   *    - if @type is `date`, expect value to be date string in yyyy-mm-dd
   *    - if @type is `amount`, expect value to be number or number string
   * @returns {number|string} - if return type is string, it would be date string with yyyy-mm-dd format
   */
  static async calculatePremium(type = 'date', memberNumber, startDate, memberInput) {
    try {
      let response;

      switch (type.toLowerCase()) {
        case 'amount':
          response = await axiosRequest().get(Services.MY_COVER.PREMIUM_CALCULATOR.GET_RETRIEVE_PAYMENT_DATE(memberNumber), {
            params: {
              amount: Number(memberInput),
              startDate,
            },
          });
          break;

        default:
          response = await PublicSiteServices.getProrataPrice(memberNumber, {
            params: {
              currentDate: getCurrentDate(),
              requiredPayDate: getCurrentDate(),
              requiredDatePaidTo: formatFormDate(memberInput),
            },
          });
          break;
      }

      if (type.toLowerCase() === 'amount' && handleHttpResponse(response) && response.data.datePaidTo) {
        return response.data.datePaidTo;
      }

      // because if `type` is `date`, we are using exiting api which already return `xx.data` hence instead of checking `response.data.initialPayment`, we should check `response.initialPayment`
      if (isSuccessfulResponse(response) && response.initialPayment) {
        return response.initialPayment;
      }

      throw new DHLError(`Unexpected data structure returned for data: ${JSON.stringify(response.data)}`);
    } catch (error) {
      throw new DHLError(`calculatePremium() - Call failed with error: ${JSON.stringify(error.message)}`, error);
    }
  }
}
